import React, { useEffect, useMemo, useState } from 'react';
import { Shield, Eye, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { workspaceAdminAPI } from '../../services/api';
import { getErrorMessage } from '../../lib/utils';

// Radix, not a native <select>. The dev server's visual-edits Babel plugin
// wraps mapped JSX children in a <span style="display:contents">, which is not
// legal inside <select> — the browser drops every <option> and the dropdown
// comes up empty. Production builds do not run that plugin, so this only broke
// in dev, which is the worst place for it to break.

const DEFAULT_DURATION = 30;

/** Left-aligned key, value after it — the audit preview reads as a record. */
const Row = ({ label, children }) => (
  <div className="flex gap-3">
    <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 break-words text-foreground">{children}</span>
  </div>
);

/**
 * Consent gate for entering someone else's tenant.
 *
 * Nothing here is decorative. The reason codes come from the proxy_reason_codes
 * master scoped to the caller's role, the consent state is the target tenant's
 * own record and blocks the session when withdrawn, the restriction list is
 * enforced per request by proxy_restrictions, and the session id is reserved
 * server-side so the record previewed is the record written.
 */
const ProxySessionDialog = ({ open, request, onCancel, onConfirm }) => {
  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // A fresh decision each time — never carry the last justification forward.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    setContext(null);
    setLoadError('');
    setReasonCode('');
    setDuration(DEFAULT_DURATION);
    setJustification('');
    setSubmitting(false);

    workspaceAdminAPI
      .getProxySessionContext(request?.tenantSlug)
      .then(({ data }) => {
        if (cancelled) return;
        setContext(data);
        setReasonCode(data?.reason_codes?.[0]?.code || '');
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(getErrorMessage(error, 'Could not load the proxy session context'));
      });

    return () => { cancelled = true; };
  }, [open, request?.tenantSlug]);

  const reasonCodes = useMemo(() => context?.reason_codes || [], [context]);
  const selected = useMemo(
    () => reasonCodes.find((r) => r.code === reasonCode) || null,
    [reasonCodes, reasonCode]
  );
  const durations = useMemo(
    () => (selected?.allowed_durations?.length ? selected.allowed_durations : context?.durations || []),
    [selected, context]
  );

  // A reason with a shorter ceiling must not leave a longer choice selected.
  useEffect(() => {
    if (!durations.length || durations.includes(duration)) return;
    setDuration(durations.includes(DEFAULT_DURATION) ? DEFAULT_DURATION : Math.max(...durations));
  }, [durations, duration]);

  const consent = context?.consent;
  const consentBlocks = Boolean(consent) && consent.allowed === false;
  const minChars = context?.justification_min ?? 20;
  const trimmed = justification.trim();
  const isValid = Boolean(reasonCode) && trimmed.length >= minChars && !consentBlocks;

  const targetName = context?.target_tenant?.name || request?.tenantName || request?.tenantSlug || 'this tenant';

  const confirm = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm({
        reason_code: reasonCode,
        justification: trimmed,
        duration_minutes: duration,
        session_id: context?.session_id,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel?.(); }}>
      {/* Flex column with a scrolling middle: the action bar stays put while
          the audit record scrolls behind it. */}
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <div className="border-b px-6 py-5">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Platform operator action
          </p>
          {/* DialogTitle/Description rather than plain tags: Radix names the
              dialog from them for screen readers, and warns when they are absent. */}
          <DialogTitle className="mt-1 text-2xl font-bold text-foreground">
            Start a proxy session
          </DialogTitle>
          <DialogDescription className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
            You will act as a <span className="font-medium text-foreground">workspace administrator</span>{' '}
            inside <span className="font-medium text-foreground">{targetName}</span>. Everything you do is
            attributed to you, not to them.
          </DialogDescription>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {loadError ? (
            <p className="text-sm text-destructive">{loadError}</p>
          ) : !context ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading session context…
            </div>
          ) : (
            <>
              {consent && (
                <div
                  className={`flex gap-3 rounded-lg border p-4 ${
                    consentBlocks
                      ? 'border-destructive/40 bg-destructive/5'
                      : 'border-amber-500/40 bg-amber-500/10'
                  }`}
                >
                  <Shield
                    className={`mt-0.5 h-5 w-5 shrink-0 ${
                      consentBlocks ? 'text-destructive' : 'text-amber-600'
                    }`}
                  />
                  <div className="space-y-0.5">
                    <p
                      className={`text-[12.5px] font-semibold ${
                        consentBlocks ? 'text-destructive' : 'text-amber-700'
                      }`}
                    >
                      Consent state
                    </p>
                    <p
                      className={`text-[12px] ${
                        consentBlocks ? 'text-destructive' : 'text-amber-700'
                      }`}
                    >
                      {consent.headline} {consent.detail}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-[13px]">
                    Reason code <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={reasonCode}
                    disabled={consentBlocks}
                    onValueChange={(value) => setReasonCode(value)}
                  >
                    <SelectTrigger className="bg-muted/40 text-[13px]">
                      <SelectValue placeholder="Choose a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {reasonCodes.map((r) => (
                        <SelectItem key={r.code} value={r.code}>
                          {r.requirement ? `${r.label} — ${r.requirement}` : r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[13px]">
                    Session duration <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={String(duration)}
                    disabled={consentBlocks}
                    onValueChange={(value) => setDuration(Number(value))}
                  >
                    <SelectTrigger className="bg-muted/40 text-[13px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {durations.map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {m} minutes{m === Math.max(...durations) ? ' (maximum)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Sessions end on their own. There is no extension.
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[13px]">
                  Justification <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  rows={3}
                  className="bg-muted/40 text-[13px] md:text-[13px]"
                  value={justification}
                  disabled={consentBlocks}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder={`What are you trying to see or fix? Minimum ${minChars} characters.`}
                />
                <p
                  className={`text-xs ${
                    trimmed.length >= minChars ? 'text-muted-foreground' : 'text-amber-600'
                  }`}
                >
                  {trimmed.length} / {minChars} characters
                  {trimmed.length >= minChars ? '' : ' — keep going'}
                </p>
              </div>

              <div>
                <p className="mb-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  This is the audit record that will be written
                </p>
                <div className="space-y-1 rounded-md border-l-4 border-l-amber-500 bg-muted/50 p-4 font-mono text-xs leading-relaxed">
                  <Row label="session_id">{context.session_id} (reserved)</Row>
                  <Row label="actor">
                    {context.actor?.email} · {context.actor?.role}
                  </Row>
                  <Row label="target_tenant">
                    {context.target_tenant
                      ? `TNT-${context.target_tenant.id} · ${context.target_tenant.name}`
                      : '—'}
                  </Row>
                  <Row label="target_user">{context.target_user?.label}</Row>
                  <Row label="region">{context.region}</Row>
                  <Row label="reason_code">
                    {reasonCode || '—'}
                    {selected ? ` · ${selected.label}` : ''}
                  </Row>
                  <Row label="justification">{trimmed || '—'}</Row>
                  <Row label="consent">
                    {consent?.state}
                    {consent?.expires_at ? ` (exp ${String(consent.expires_at).slice(0, 10)})` : ''}
                  </Row>
                  <Row label="cap_minutes">{duration}</Row>
                  <Row label="restrictions">{context.restrictions_line}</Row>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Fixed action bar: what is blocked stays visible next to the button
            that starts the session. */}
        <div className="flex shrink-0 flex-col gap-3 border-t bg-background px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-xs text-muted-foreground">
            Blocked: {(context?.restrictions || []).join(' · ') || '—'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" className="text-[13px]" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button className="text-[13px]" onClick={confirm} disabled={!isValid || submitting}>
              <Eye className="mr-2 h-4 w-4" />
              {submitting ? 'Starting…' : 'Start session'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProxySessionDialog;
